import './PickupFromStore.scss';

import { any, bool, func, string } from 'prop-types';
import React, { cloneElement, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { getAvailabilityStoresSelector } from '@redux/reducers/bopis/getAvailabilityStores/selector';
import {
  getBOPISFreeGiftAvailabilityStoresSelector
} from '@redux/reducers/bopis/getBOPISFreeGiftAvailabilityStores/selector';
import { DELIVERY_OPTION } from '@utils/constant';

import BOShippingInfoLayout from '../BOShippingInfoLayout';
import AvailabilityStoresListModal from './AvailabilityStoresListModal';
import { useCheckBOPISAvailable } from './hooks';
import PickupFromStoreContext from './PickupFromStoreContext';

const PickupFromStore = ({
  name,
  addressSetting,
  textClassName = '',
  isShowIcon = true,
  selectStoreButtonLabel = '',
  openStoresListModalLabel = null,
  onStoreSelect,
  isShowStoresListProp
}) => {
  const checkBOPISAvailableService = useCheckBOPISAvailable();

  const {
    isFetchingStores,
    data: bopisVariantStores
  } = useSelector(getAvailabilityStoresSelector);

  const freeGiftsSelectedID = useSelector(state => state.buyingOptionReducer.BOFreeGiftsReducer.freeGiftsSelectedID)
    || [];

  const {
    isFetchingStores: isFetchingBOPISFreeGiftStores,
    data: bopisFreeGiftStores
  } = useSelector(getBOPISFreeGiftAvailabilityStoresSelector);
  const settingGlobal = useSelector((state) => state.settingGlobalReducer.settingGlobal);
  const messageObj = useSelector((state) => state.getMessageReducer.objMessages);
  const [isShowModal, setIsShowModal] = useState(false);
  const [keyword, setKeyWord] = useState('');
  const [filterAreaValue, setFilterAreaValue] = useState(null);

  const { isShowStoresList } = useMemo(() => checkBOPISAvailableService(), [checkBOPISAvailableService]);

  const mappingBOPISStoresList = useMemo(() => {
    let storesListTemp = [];
    if (bopisVariantStores?.length && bopisFreeGiftStores?.length && !freeGiftsSelectedID?.length) {
      const concatAllStores = [...bopisVariantStores, ...bopisFreeGiftStores];
      for (let i = 0; i <= concatAllStores.length; i++) {
        for (let j = i + 1; j < concatAllStores.length; j++) {
          if (concatAllStores[i].OutletId === concatAllStores[j].OutletId) {
            storesListTemp.push(concatAllStores[i]);
          }
        }
      }
    } else {
      storesListTemp = [...bopisVariantStores];
    }

    return storesListTemp;
  }, [bopisVariantStores, bopisFreeGiftStores, freeGiftsSelectedID]);

  const storesList = useMemo(() => {
    if (isShowModal) {
      if (Array.isArray(mappingBOPISStoresList) && mappingBOPISStoresList.length) {
        const filterData = mappingBOPISStoresList.filter((store) =>
          // eslint-disable-next-line no-unused-vars
          filterAreaValue ? Object.entries(filterAreaValue)
            // eslint-disable-next-line no-unused-vars
            ?.filter(([_, value]) => value)
            ?.every(([key, value]) => store?.[key] === value) : true
        );

        if (keyword && filterAreaValue) {
          return [...filterData].filter((store) => store?.Name?.toLowerCase()?.includes(
            keyword));
        }
        if (filterAreaValue) return [...filterData];

        return mappingBOPISStoresList.filter((store) => store?.Name?.toLowerCase().includes(keyword));
      }

      return [];
    } else {
      /** INFO: reset store list to default when close the modal */
      setKeyWord('');

      setFilterAreaValue(null);

      return mappingBOPISStoresList;
    }
  }, [mappingBOPISStoresList, keyword, filterAreaValue, isShowModal]);

  const handleOpenModal = useCallback(() => setIsShowModal(true), []);
  const handleCloseModal = useCallback(() => setIsShowModal(false), []);
  const handleSearchStore = useCallback((keyword) => setKeyWord(keyword), []);
  const handleFilterArea = useCallback((params) => setFilterAreaValue(params), []);
  const handleClearFilter = useCallback(() => setFilterAreaValue(null), []);

  return !isFetchingStores && !isFetchingBOPISFreeGiftStores ? (
    <PickupFromStoreContext.Provider
      value={{
        addressSetting,
        storesList,
        selectStoreButtonLabel,
        onSearchStore: handleSearchStore,
        onFilterArea: handleFilterArea,
        onClearFilter: handleClearFilter,
        onStoreSelect
      }}
    >
      <BOShippingInfoLayout
        name={name}
        shippingOptionType={isShowIcon ? DELIVERY_OPTION.PickUpStore : null}
        wrapperClassName='bopis-bo-info-layout'
      >
        {(
          typeof isShowStoresListProp !== 'undefined' ? isShowStoresListProp : isShowStoresList
        ) && mappingBOPISStoresList.length ? (
            <>
              {openStoresListModalLabel ? (
                cloneElement(openStoresListModalLabel, {
                  onClick: handleOpenModal
                })
              ) : (
                <>
                  <p className={['shipping-info__item__text__desc', textClassName].join(' ')}>
                    {settingGlobal?.['AvailableAtText']}{' '}
                    <span className='link-store' onClick={handleOpenModal}>
                      {mappingBOPISStoresList.length} {settingGlobal?.['NumberOfStoreText']}
                    </span>
                  </p>
                  <p
                    className={[
                      'shipping-info__item__text__desc',
                      textClassName
                    ].join(' ')}
                  >{settingGlobal?.['TimeAfterOrderPlacedText']}</p>
                </>
              )}
              <AvailabilityStoresListModal isShow={isShowModal} onCloseHandler={handleCloseModal} />
            </>
          ) : (
            <p className={['shipping-info__item__text__desc', textClassName].join(' ')}>{messageObj?.['Msg_020_1']}</p>
          )}
      </BOShippingInfoLayout>
    </PickupFromStoreContext.Provider>
  ) : (
    <div className='shipping-info__item shipping-info--delivery bopis-bo-info-skeleton'></div>
  );
};

PickupFromStore.propTypes = {
  name: string,
  textClassName: string,
  addressSetting: any,
  isShowIcon: bool,
  selectStoreButtonLabel: string,
  openStoresListModalLabel: any,
  onStoreSelect: func,
  isShowStoresListProp: bool
};

export default PickupFromStore;